/**
 * What `data_file` lines mean, and which ones are real.
 *
 * A `.meta` file sitting in a resource with no `data_file` line is inert. The
 * game never reads it, nothing errors, and the vehicle simply does nothing after
 * install — which is why "my mod doesn't work" is so often one missing line.
 * `META_DATA_FILE_KINDS` is the table that lets a tool say which line is missing
 * instead of shrugging.
 *
 * The second table is the other half. FiveM matches the `data_file` type string
 * against a fixed list; a typo like `VEHICLE_METADATA` instead of
 * `VEHICLE_METADATA_FILE` is silently ignored at load, so the resource starts
 * clean and the content never appears.
 *
 * ## Two ways a file gets loaded, and why that is two tables
 *
 * Everything above is about declaration. The bottom of the file is about the
 * other route: `STREAM_EXTENSIONS` is the set FiveM loads with no declaration
 * at all, by bare basename, flat across the whole server.
 *
 * Those two routes are the complete list, and an extension belongs to exactly
 * one of them. That is why `MANIFEST_DECLARED_EXTENSIONS` exists rather than
 * being folded into the streamed list: `.rel`, `.dat` and `.gxt2` are part of
 * the namespace two resources can fight over, but they fight over the manifest
 * line, not the file name, and every consumer of `STREAM_EXTENSIONS` reads
 * membership as "this file is correct precisely because nothing declares it".
 * For these three that reading is backwards.
 *
 * ## On completeness
 *
 * `KNOWN_DATA_FILE_TYPES` is what this repo has seen and can vouch for, not
 * every string the engine will accept — RAGE's table is long, partly
 * undocumented, and grows with game builds. That is why an unrecognised type is
 * reported as a warning that says "not one this tool knows", never as an error
 * that says "wrong". Being confidently wrong about a rare-but-valid type would
 * cost more trust than the check earns.
 */

/**
 * Meta file name → the `data_file` type that has to declare it.
 *
 * Seeded from `desktop/src/lib/resourceScan.ts`, which is where these first
 * earned their keep, and extended with the rest of the vehicle and ped set.
 */
export const META_DATA_FILE_KINDS: Record<string, string> = {
  "vehicles.meta": "VEHICLE_METADATA_FILE",
  "handling.meta": "HANDLING_FILE",
  "carcols.meta": "CARCOLS_FILE",
  "carvariations.meta": "VEHICLE_VARIATION_FILE",
  "vehiclelayouts.meta": "VEHICLE_LAYOUTS_FILE",
  "dlctext.meta": "DLC_TEXT_FILE",
  "contentunlocks.meta": "CONTENT_UNLOCKING_META_FILE",
  "caraddoncontentunlocks.meta": "CONTENT_UNLOCKING_META_FILE",
  "weaponarchetypes.meta": "WEAPON_METADATA_FILE",
  "vehicleweapons.meta": "WEAPONINFO_FILE",
  "peds.meta": "PED_METADATA_FILE",
  "pedpersonality.meta": "PED_PERSONALITY_FILE",
  "explosion.meta": "EXPLOSION_INFO_FILE",
  "scenarios.meta": "SCENARIO_INFO_FILE",
  "shop_vehicle.meta": "VEHICLE_SHOP_DLC_FILE",
  "vehiclemodelsets.meta": "VEHICLE_MODELSETS_FILE",
  "loadouts.meta": "LOADOUTS_FILE",
  "weaponcomponents.meta": "WEAPONCOMPONENTSINFO_FILE",
  "weaponanimations.meta": "WEAPON_ANIMATIONS_FILE",
  "clip_sets.xml": "CLIP_SETS_FILE",
  "vehiclelayouts.xml": "VEHICLE_LAYOUTS_FILE",
};

/**
 * The XML root element each kind must actually have.
 *
 * A `data_file 'HANDLING_FILE' 'vehicles.meta'` is well-formed Lua naming a
 * well-formed XML file, and it loads nothing: the game reads the file expecting
 * a `<CHandlingDataMgr>` and finds something else. Cheap to check, and totally
 * silent when wrong, which is the worst combination a bug can have.
 *
 * VERIFIED. Every root below was read off Rockstar's own shipped `.meta` files
 * by `scripts/gta/dump-archives` (build 1.0.3889.0) rather than remembered. Two
 * of them are not what a reasonable person would guess — a content-unlocking
 * file's root is `SContentUnlocks`, not the longer name its `data_file` type
 * implies, and `dlctext.meta` is a `CExtraTextMetaFile` — which is the whole
 * argument for checking rather than asserting. A kind with no verified root is
 * absent from this table, and an absent kind is skipped rather than guessed.
 */
export const DATA_FILE_ROOTS: Record<string, string> = {
  VEHICLE_METADATA_FILE: "CVehicleModelInfo__InitDataList",
  HANDLING_FILE: "CHandlingDataMgr",
  CARCOLS_FILE: "CVehicleModelInfoVarGlobal",
  VEHICLE_VARIATION_FILE: "CVehicleModelInfoVariation",
  VEHICLE_LAYOUTS_FILE: "CVehicleMetadataMgr",
  PED_METADATA_FILE: "CPedModelInfo__InitDataList",
  WEAPONINFO_FILE: "CWeaponInfoBlob",
  WEAPON_METADATA_FILE: "CWeaponModelInfo__InitDataList",
  CONTENT_UNLOCKING_META_FILE: "SContentUnlocks",
  EXPLOSION_INFO_FILE: "CExplosionInfoManager",
  SCENARIO_INFO_FILE: "CScenarioInfoManager",
  DLC_TEXT_FILE: "CExtraTextMetaFile",
  VEHICLE_SHOP_DLC_FILE: "ShopVehicleDataArray",
  VEHICLE_MODELSETS_FILE: "CAmbientModelSets",
};

/**
 * `data_file` type strings this repo recognises.
 *
 * Grouped the way the engine groups them, so a reader looking for "is there an
 * audio one" finds the audio block rather than scanning an alphabetical wall.
 */
export const KNOWN_DATA_FILE_TYPES: readonly string[] = [
  // Audio
  "AUDIO_GAMEDATA",
  "AUDIO_SOUNDDATA",
  "AUDIO_SPEECHDATA",
  "AUDIO_SYNTHDATA",
  "AUDIO_WAVEPACK",
  "AUDIO_DYNAMIXDATA",

  // Vehicles
  "CARCOLS_FILE",
  "HANDLING_FILE",
  "VEHICLE_METADATA_FILE",
  "VEHICLE_VARIATION_FILE",
  "VEHICLE_LAYOUTS_FILE",
  "VEHICLE_MODELSETS_FILE",
  "VEHICLE_SHOP_DLC_FILE",

  // Peds
  "PED_METADATA_FILE",
  "PED_PERSONALITY_FILE",
  "PEDSTATS_FILE",
  "PED_BOUNDS_FILE",

  // Weapons
  "WEAPONINFO_FILE",
  "WEAPONINFO_FILE_PATCH",
  "WEAPON_METADATA_FILE",
  "WEAPONCOMPONENTSINFO_FILE",
  "WEAPON_ANIMATIONS_FILE",
  "LOADOUTS_FILE",

  // World and content
  "DLC_ITYP_REQUEST",
  "DLC_TEXT_FILE",
  "DLC_WEAPON_FILE",
  "CONTENT_UNLOCKING_META_FILE",
  "SCENARIO_INFO_FILE",
  "SCENARIO_POINTS_FILE",
  "SCENARIO_POINTS_PSO_FILE",
  "EXPLOSION_INFO_FILE",
  "INTERIOR_PROXY_ORDER_FILE",
  "POPGROUP_FILE",
  "POPSCHED_FILE",
  "RELATIONSHIPS_FILE",
  "TIMECYCLEMOD_FILE",

  // Animation
  "CLIP_SETS_FILE",
  "EXPRESSION_SETS_FILE",
  "FACIAL_CLIPSET_GROUPS_FILE",

  // Text and shops
  "TEXTFILE_METAFILE",
  "SHOP_PED_APPAREL_META_FILE",
  "SHOP_WEAPON_META_FILE",
];

const KNOWN_TYPES = new Set(KNOWN_DATA_FILE_TYPES);

export function isKnownDataFileType(type: string): boolean {
  return KNOWN_TYPES.has(type.trim().toUpperCase());
}

/** The `data_file` type a meta file needs, or `null` if it needs none. */
export function dataFileKindFor(fileName: string): string | null {
  return META_DATA_FILE_KINDS[fileName.trim().toLowerCase()] ?? null;
}

/** The XML root a `data_file` type expects, or `null` if we do not check it. */
export function expectedRootFor(type: string): string | null {
  return DATA_FILE_ROOTS[type.trim().toUpperCase()] ?? null;
}

/**
 * Extensions FiveM streams automatically out of any folder named `stream`.
 *
 * Nothing here needs a `data_file` line, and warning about it would be the
 * inverse of the missing-declaration check: correct precisely *because* it is
 * undeclared. That is the whole membership test, and it is narrower than "every
 * extension a conflict checker should look at" — see
 * `MANIFEST_DECLARED_EXTENSIONS` below for the other half and why the two are
 * not one list.
 *
 * Thirteen, not the nine `{yft, ytd, ydr, ydd, ybn, ycd, awc, ynv, ypt}` a
 * model-and-texture reading of "stream asset" produces: `.ymap`, `.ymt`,
 * `.ytyp` and `.ynd` stream by bare basename too, and a map or clothing pack is
 * made of nothing else.
 */
export const STREAM_EXTENSIONS: readonly string[] = [
  "yft",
  "ytd",
  "ydr",
  "ydd",
  "ybn",
  "ycd",
  "awc",
  "ynv",
  "ypt",
  "ymap",
  "ymt",
  "ytyp",
  "ynd",
];

const STREAM_EXTENSION_SET = new Set(STREAM_EXTENSIONS);

export function isStreamExtension(extension: string): boolean {
  return STREAM_EXTENSION_SET.has(extension.trim().toLowerCase().replace(/^\./, ""));
}

/**
 * Extensions that reach the game only because a manifest line names them.
 *
 * The other half of the namespace, and deliberately not part of
 * `STREAM_EXTENSIONS`, because for these three the bare basename is not the
 * thing that collides. Drop `dlcambo_game.dat151.rel` into a `stream` folder
 * and nothing happens: the audio bank is loaded because a manifest said
 * `data_file 'AUDIO_GAMEDATA' 'audioconfig/dlcambo_game'`, and two resources
 * clash when they register the same *declaration*, not when they ship the same
 * file name. Matching them by basename would also miss the common real clash,
 * where two packs register `HANDLING_FILE handling.meta` from two different
 * folders and the file names never collide at all.
 *
 * Keeping them out of `STREAM_EXTENSIONS` is not pedantry, it is the difference
 * between a correct pack passing and failing. `STREAM_EXTENSIONS` is read by
 * four shipped surfaces that treat membership as "undeclared is correct":
 * `analyze.ts` skips the missing-declaration check for anything in it, and Code
 * Smith counts everything in it as a shipped asset and reports the ones no Lua
 * names as possibly unused. A correctly declared `audioconfig/*.rel` satisfies
 * neither reading — it is declared, and no Lua will ever mention it, because
 * the `data_file` value is the stem `audioconfig/dlcambo_game` and the file on
 * disk is `dlcambo_game.dat151.rel`. Adding it to the streamed list turns a
 * correct audio-enabled vehicle pack into four new findings about files that
 * are doing exactly the right thing.
 *
 * So: a tool that wants the whole conflict namespace unions the two lists and
 * keys each half the way that half is actually resolved.
 */
export const MANIFEST_DECLARED_EXTENSIONS: readonly string[] = [
  // Compiled audio metadata: `*.dat151.rel`, `*.dat54.rel`, and friends.
  "rel",
  // `audioconfig` sidecars and the loose world data files, `.dat` either way.
  "dat",
  // Compiled text tables, declared through the manifest's `files` block.
  "gxt2",
];

const MANIFEST_DECLARED_EXTENSION_SET = new Set(MANIFEST_DECLARED_EXTENSIONS);

/** True for an extension that only loads because a manifest line declares it. */
export function isManifestDeclaredExtension(extension: string): boolean {
  return MANIFEST_DECLARED_EXTENSION_SET.has(
    extension.trim().toLowerCase().replace(/^\./, ""),
  );
}
